import { useEffect, useMemo, useState } from "react";
import { Search, X, Save, CheckCircle2, Edit3 } from "lucide-react";
import { api, imgUrl, formatApiErrorDetail } from "@/lib/api";

// Product Editor: edit copy (name/description/notes/benefits/sequence/MW/etc.)
export default function AdminProductEditor() {
    const [products, setProducts] = useState([]);
    const [query, setQuery] = useState("");
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null); // currently editing product object
    const [error, setError] = useState("");

    useEffect(() => {
        api.get("/admin/products")
            .then((r) => setProducts(r.data))
            .catch((e) => setError(formatApiErrorDetail(e.response?.data?.detail) || e.message))
            .finally(() => setLoading(false));
    }, []);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return products;
        return products.filter((p) =>
            [p.name, p.slug, p.short_name, p.category, p.description].some((v) =>
                (v || "").toLowerCase().includes(q)
            )
        );
    }, [products, query]);

    const onSaved = (updated) => {
        setProducts((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));
        setEditing(null);
    };

    return (
        <div data-testid="admin-products">
            <div className="mb-6 sm:mb-8">
                <div className="text-[10px] uppercase tracking-[0.28em] text-[#0077b6] mb-2">// Product Copy</div>
                <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold uppercase tracking-tighter">
                    Product editor
                </h1>
                <p className="text-sm text-neutral-500 mt-2">
                    Edit product name, descriptions, sequence, benefits, and metadata. Edits persist
                    across deployments.
                </p>
            </div>

            <div className="relative max-w-md mb-6">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-500" />
                <input
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search products"
                    className="w-full bg-transparent border border-neutral-200 focus:border-[#00aeef] focus:outline-none rounded-lg pl-10 pr-3 py-2.5 text-sm"
                    data-testid="products-search"
                />
            </div>

            {error && (
                <div className="border border-red-500/40 bg-red-500/10 text-red-700 text-xs p-3 mb-4 rounded-lg" data-testid="products-error">
                    {error}
                </div>
            )}

            {loading ? (
                <div className="text-neutral-500 text-sm">Loading products…</div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                    {filtered.map((p) => (
                        <button
                            key={p.id}
                            type="button"
                            onClick={() => setEditing(p)}
                            className="text-left border border-neutral-200 rounded-lg p-4 hover:border-[#0089c8] transition-colors group"
                            data-testid={`prod-card-${p.slug}`}
                        >
                            <div className="flex items-start gap-3">
                                <div className="w-14 h-14 bg-white border border-neutral-200 rounded-md overflow-hidden flex-shrink-0">
                                    <img src={imgUrl(p.image)} alt={p.name} className="w-full h-full object-cover" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="font-display font-bold truncate group-hover:text-black">{p.name}</div>
                                    <div className="text-xs text-neutral-500 truncate">{p.category}</div>
                                    <div className="text-[10px] text-neutral-500 mt-1 truncate font-mono">{p.slug}</div>
                                    {p.admin_edited && (
                                        <div className="text-[10px] uppercase tracking-[0.18em] text-[#0077b6] mt-1">edited</div>
                                    )}
                                </div>
                                <Edit3 className="w-4 h-4 text-neutral-500 group-hover:text-black" />
                            </div>
                        </button>
                    ))}
                </div>
            )}

            {editing && <EditorDrawer product={editing} onClose={() => setEditing(null)} onSaved={onSaved} />}
        </div>
    );
}

function EditorDrawer({ product, onClose, onSaved }) {
    const [draft, setDraft] = useState({
        name: product.name || "",
        short_name: product.short_name || "",
        category: product.category || "",
        description: product.description || "",
        notes: product.notes || "",
        benefits: (product.benefits || []).join("\n"),
        sequence: product.sequence || "",
        molecular_weight: product.molecular_weight || "",
        purity: product.purity || "",
        dosage: product.dosage || "",
        storage: product.storage || "",
        featured: !!product.featured,
        in_stock: product.in_stock !== false,
    });
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);
    const [error, setError] = useState("");

    const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        setError("");
        try {
            const payload = {
                ...draft,
                benefits: draft.benefits
                    .split("\n")
                    .map((s) => s.trim())
                    .filter(Boolean),
            };
            const { data } = await api.put(`/admin/products/${product.id}`, payload);
            setSaved(true);
            setTimeout(() => onSaved(data), 700);
        } catch (e) {
            setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex justify-end bg-white/70 backdrop-blur-sm" data-testid="editor-drawer">
            <button type="button" onClick={onClose} aria-label="Close" className="absolute inset-0" />
            <aside className="relative w-full max-w-2xl bg-white border-l border-neutral-200 overflow-y-auto">
                <div className="flex items-center justify-between px-5 sm:px-6 py-4 sm:py-5 border-b border-neutral-200 sticky top-0 bg-white z-10">
                    <div className="min-w-0">
                        <div className="text-[10px] uppercase tracking-[0.28em] text-[#0077b6]">// Editing</div>
                        <div className="font-display font-bold text-base sm:text-lg truncate">{product.name}</div>
                    </div>
                    <button type="button" onClick={onClose} className="p-2 hover:text-black flex-shrink-0" data-testid="editor-close">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <form onSubmit={submit} className="px-5 sm:px-6 py-5 sm:py-6 space-y-5">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Field label="Name">
                            <input value={draft.name} onChange={(e) => set("name", e.target.value)} className={INP} data-testid="editor-name" />
                        </Field>
                        <Field label="Short name">
                            <input value={draft.short_name} onChange={(e) => set("short_name", e.target.value)} className={INP} data-testid="editor-short-name" />
                        </Field>
                    </div>
                    <Field label="Category">
                        <input value={draft.category} onChange={(e) => set("category", e.target.value)} className={INP} data-testid="editor-category" />
                    </Field>
                    <Field label="Description">
                        <textarea value={draft.description} onChange={(e) => set("description", e.target.value)} rows={4} className={TXT} data-testid="editor-description" />
                    </Field>
                    <Field label="Notes / advisory">
                        <textarea value={draft.notes} onChange={(e) => set("notes", e.target.value)} rows={3} className={TXT} data-testid="editor-notes" />
                    </Field>
                    <Field label="Benefits (one per line)">
                        <textarea value={draft.benefits} onChange={(e) => set("benefits", e.target.value)} rows={4} className={`${TXT} font-mono text-xs`} data-testid="editor-benefits" />
                    </Field>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Field label="Sequence">
                            <input value={draft.sequence} onChange={(e) => set("sequence", e.target.value)} className={`${INP} font-mono`} data-testid="editor-sequence" />
                        </Field>
                        <Field label="Molecular weight">
                            <input value={draft.molecular_weight} onChange={(e) => set("molecular_weight", e.target.value)} className={INP} data-testid="editor-mw" />
                        </Field>
                        <Field label="Purity">
                            <input value={draft.purity} onChange={(e) => set("purity", e.target.value)} className={INP} data-testid="editor-purity" />
                        </Field>
                        <Field label="Dosage / format">
                            <input value={draft.dosage} onChange={(e) => set("dosage", e.target.value)} className={INP} data-testid="editor-dosage" />
                        </Field>
                    </div>
                    <Field label="Storage">
                        <input value={draft.storage} onChange={(e) => set("storage", e.target.value)} className={INP} data-testid="editor-storage" />
                    </Field>

                    <div className="flex items-center gap-6">
                        <Toggle label="Featured" checked={draft.featured} onChange={(v) => set("featured", v)} testid="editor-featured" />
                        <Toggle label="In stock" checked={draft.in_stock} onChange={(v) => set("in_stock", v)} testid="editor-in-stock" />
                    </div>

                    {error && (
                        <div className="border border-red-500/40 bg-red-500/10 text-red-700 text-xs p-3 rounded-lg" data-testid="editor-error">
                            {error}
                        </div>
                    )}

                    <div className="flex gap-3 pt-2 border-t border-neutral-200 sticky bottom-0 bg-white py-4 -mx-5 sm:-mx-6 px-5 sm:px-6">
                        <button
                            type="submit"
                            disabled={saving}
                            className="inline-flex items-center gap-2 bg-[#f8f8f8] hover:bg-neutral-800 disabled:opacity-50 rounded-lg text-black font-bold uppercase tracking-[0.18em] text-xs px-5 py-3"
                            data-testid="editor-save"
                        >
                            {saved ? (
                                <>
                                    <CheckCircle2 className="w-4 h-4" /> saved
                                </>
                            ) : (
                                <>
                                    <Save className="w-4 h-4" /> {saving ? "saving…" : "save changes"}
                                </>
                            )}
                        </button>
                        <button
                            type="button"
                            onClick={onClose}
                            className="text-xs uppercase tracking-[0.18em] text-neutral-600 hover:text-neutral-900 px-4 py-3"
                        >
                            Cancel
                        </button>
                    </div>
                </form>
            </aside>
        </div>
    );
}

const INP = "w-full bg-transparent border border-neutral-200 focus:border-[#00aeef] focus:outline-none rounded-lg px-3 py-2 text-sm text-neutral-900";
const TXT = "w-full bg-transparent border border-neutral-200 focus:border-[#00aeef] focus:outline-none rounded-lg px-3 py-2 text-sm text-neutral-900 resize-none";

function Field({ label, children }) {
    return (
        <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-neutral-500 mb-1.5">{label}</div>
            {children}
        </div>
    );
}

function Toggle({ label, checked, onChange, testid }) {
    return (
        <label className="inline-flex items-center gap-3 cursor-pointer select-none">
            <input
                type="checkbox"
                checked={checked}
                onChange={(e) => onChange(e.target.checked)}
                className="sr-only peer"
                data-testid={testid}
            />
            <span className="w-9 h-5 bg-white/10 peer-checked:bg-[#f8f8f8] rounded-full relative transition-colors">
                <span className={`absolute top-0.5 ${checked ? "left-4" : "left-0.5"} w-4 h-4 bg-white rounded-full transition-all`} />
            </span>
            <span className="text-[10px] uppercase tracking-[0.18em] text-neutral-700">{label}</span>
        </label>
    );
}
